import { useCallback, useState } from 'react';

import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  LookbackOption,
  MixpanelEvent,
  QUOTE_STATUS_MAPPING,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  formattedDateForSubscription,
  tabLabelerEnumerated,
  useConstant,
  useDynamicCallback,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type Column,
  type PersistedDefaultTabProps,
  type Quote,
  type TabProps,
} from '@talos/kyoko';
import type { RowDoubleClickedEvent } from 'ag-grid-community';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { QuotesBlotterTable } from './QuotesBlotter';
import type { QuotesTableFilter } from './useQuotesFilter';

export interface QuoteTabProps extends TabProps {
  defaultFilter?: QuotesTableFilter;
  defaultColumns?: Partial<Column>[];
}

const PRESET_TABS: PersistedDefaultTabProps<QuoteTabProps>[] = [
  {
    label: 'Open',
    id: 'open-quotes',
    closable: false,
    editable: false,
    defaultFilter: {
      _start: LookbackOption.PastWeek,
      _statuses: ['All Open'],
      Statuses: QUOTE_STATUS_MAPPING['All Open'],
    },
    defaultColumns: [],
  },
  {
    label: 'Recent',
    id: 'recent-quotes',
    closable: true,
    editable: true,
    defaultFilter: {
      _start: LookbackOption.PastWeek,
    },
    defaultColumns: [],
  },
];

const NEW_TAB_DEFAULTS: QuoteTabProps = {
  defaultFilter: {
    StartDate: formattedDateForSubscription(Sugar.Date.create('2 days ago')),
  },
  defaultColumns: [],
};

const tabLabeler = tabLabelerEnumerated('Quotes');

const QUOTES_BLOTTER_PREFIX = 'principal/quotes';
function getBlotterID(tabID: string | undefined) {
  return `${QUOTES_BLOTTER_PREFIX}/${tabID}`;
}

export function Quotes() {
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useDynamicCallback(() => setInitialFiltersOpen(false));
  const handleAdd = useDynamicCallback(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  });

  const defaultInitialItems = useConstant<PersistedDefaultTabProps<QuoteTabProps>[]>(PRESET_TABS);
  const [excludedIDs] = useState(PRESET_TABS.map(tab => tab.id || ''));

  const persistedTabs = usePersistedTabs<QuoteTabProps>(QUOTES_BLOTTER_PREFIX, {
    defaultInitialItems,
    defaultInitialSelectedIndex: 0,
    excludedIDs,
    onSelect: handleSelect,
  });

  const tabs = useTabs<QuoteTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    tabs.clone(tabs.selectedIndex);
  }, [tabs]);

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  const { openDetailsDrawer } = useDetailsDrawer();
  const handleDoubleClickRow = useDynamicCallback(({ data }: RowDoubleClickedEvent<Quote>) => {
    openDetailsDrawer({ entity: data });
  });

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large}>
      <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} reorderable={true} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map((tab, idx) => (
          <AccordionGroup key={tab.id}>
            <QuotesBlotterTable
              key={idx}
              blotterID={getBlotterID(tab.id)}
              defaultFilter={'defaultFilter' in tab ? tab.defaultFilter : NEW_TAB_DEFAULTS.defaultFilter}
              onCloneTab={handleCloneTab}
              onRowDoubleClicked={handleDoubleClickRow}
              initialIsOpen={initialFiltersOpen}
            />
          </AccordionGroup>
        ))}
      </TabPanels>
    </Tabs>
  );
}
